import { onBeforeUnmount, ref, watchEffect, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

/** A page's own title, when it has a better one than its route (a twin's name). */
const override = ref<string | null>(null);

/**
 * Keep `document.title` right, reactively.
 *
 * The first version set the title in `router.afterEach`, which runs on
 * navigation and at no other time. Switching the language left the tab reading
 * "Übersicht" over an English page until the next click, and a twin page could
 * not put the twin's name there at all, because its name arrives after the
 * navigation has finished. A title is derived state: route, language and page
 * data in, one string out, recomputed whenever any of them changes.
 */
export function useDocumentTitle(): void {
  const route = useRoute();
  const { t } = useI18n();
  watchEffect(() => {
    const page = override.value ?? t(`pages.${route.meta.title ?? 'overview'}`);
    document.title = `${page} · StadtPuls`;
  });
}

/** Give the current page its own title while it is mounted. */
export function usePageTitle(title: Ref<string | null | undefined>): void {
  watchEffect(() => {
    override.value = title.value ?? null;
  });
  onBeforeUnmount(() => {
    override.value = null;
  });
}
